import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { isEqual } from 'lodash-es';
import {
  DevicesStore,
  FEATURES_PANEL_TYPES,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  InstallationPanelOrder,
  RichDevice,
  defaultPanelsOrder,
} from '@store';
import { Observable, Subject } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { UIService } from '../../../../shared/services';
import { DragDropLayoutService } from '../../drag-drop-layout/drag-drop-layout.service';
import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  PowerLimitationMethod,
  PowerLimitationOverviewProperties,
  PowerLimitationType,
} from '@vi-iot/client/index';
import { Unit } from 'src/app/shared/common/units';

@Component({
  selector: 'app-wallbox-feature-tiles',
  templateUrl: './wallbox-feature-tiles.component.html',
  styleUrls: ['./wallbox-feature-tiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WallboxFeatureTilesComponent implements OnInit, OnDestroy {
  readonly FeaturePropertyKey = FeaturePropertyKey;

  orderKey: string;
  panels: InstallationPanelOrder[];
  panelTypes = FEATURES_PANEL_TYPES;
  isDragDropDisabled: boolean;

  device: RichDevice;

  powerLimitationOverview$: Observable<Feature<PowerLimitationOverviewProperties, unknown>>;
  isLimitationBannerVisible: boolean;
  limitationValue: number;
  limitationUnit: Unit;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public devicesStore: DevicesStore,
    private uiService: UIService,
    private cdr: ChangeDetectorRef,
    private dragDropLayoutService: DragDropLayoutService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.devicesStore.selectedDevice$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((device) => {
      this.device = device;
    });

    this.powerLimitationOverview$ = this.installationFeaturesStore
      .selectInstallationGatewayFeature<PowerLimitationOverviewProperties, unknown>(
        FeatureNames.powerLimitationOverview
      )
      .pipe(
        tap((feature) => {
          const matchingEntry = feature?.properties.limitations.value.find(
            (entry) => entry.deviceId === this.device.id
          );
          if (matchingEntry) {
            const isConsumptionLimitation =
              matchingEntry.limitationType === PowerLimitationType.consumption;
            const isDirectMethod = matchingEntry.method === PowerLimitationMethod.direct;

            this.limitationUnit = matchingEntry.unit;
            this.limitationValue = matchingEntry.value;
            this.isLimitationBannerVisible = isConsumptionLimitation && isDirectMethod;
          }
        })
      );

    this.dragDropLayoutService.isDragDropDisabled
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isDragDropDisabled) => {
        this.isDragDropDisabled = isDragDropDisabled;
        this.cdr.detectChanges();
      });

    this.installationDetailsStore.gatewaySerial$
      .pipe(
        distinctUntilChanged(isEqual),
        takeUntil(this.ngUnsubscribe$),
        filter((gatewaySerial) => Boolean(gatewaySerial))
      )
      .subscribe((gatewaySerial) => {
        this.installationDetailsStore.getPanelsOrder(gatewaySerial);
        this.cdr.detectChanges();
      });

    this.uiService.idOfOpenedWallboxDevice$
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((idOfWallboxDevice) => {
        this.orderKey = `wallbox-${idOfWallboxDevice}`;
        this.cdr.detectChanges();
      });

    this.installationDetailsStore
      .getPanelsOrderForDevice(this.orderKey)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((panelsOrder: InstallationPanelOrder[]) => {
        if (panelsOrder?.length > 0) {
          this.panels = panelsOrder;
        } else {
          this.panels = defaultPanelsOrder;
        }

        this.cdr.markForCheck();
      });
  }

  drop(event: CdkDragDrop<string>): void {
    this.dragDropLayoutService.updatePanelOrderListAfterDrop(
      event.previousContainer.data,
      event.container.data,
      this.panels,
      this.orderKey
    );
  }

  trackBy(_index: number, panel: InstallationPanelOrder): string {
    return panel.panel;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
